// admin-analytics-expansion P2b (tasks 2b.1) — TicketFilterBar
//
// Filter controls for the ticket browse list: location, status, paymentStatus,
// date range (from/to). Mirrors AuditLogFilterBar's presentational convention
// (holds no fetch logic of its own — the parent page owns filter state and
// the `locations` option list).
//
// R6 (spec #2559): changing ANY filter here calls `onFiltersChange`, which the
// parent page includes in `useCursorPagination`'s `queryKey` — TanStack Query
// then discards prior pages automatically, resetting pagination to page 1.
// The location switcher is naturally role-scoped: `locations` comes from the
// parent's `getMyLocations()` call, which the backend already restricts to a
// supervisor's assigned locations only (org_admin sees every org location).

import { useTranslation } from '@jq4race/i18n';
import type {
  AssignedLocationResponse,
  TicketBrowsePaymentStatus,
  TicketStatus,
} from '@jq4race/shared';
import { Button, Input } from '@jq4race/ui';
import { X } from 'lucide-react';

export interface TicketFilters {
  locationId: string;
  status: TicketStatus | '';
  paymentStatus: TicketBrowsePaymentStatus | '';
  from: string;
  to: string;
}

export const INITIAL_TICKET_FILTERS: TicketFilters = {
  locationId: '',
  status: '',
  paymentStatus: '',
  from: '',
  to: '',
};

const STATUS_OPTIONS: { value: TicketStatus; labelKey: string }[] = [
  { value: 'parked', labelKey: 'status.parked' },
  { value: 'requested', labelKey: 'status.requested' },
  { value: 'in_transit', labelKey: 'status.inTransit' },
  { value: 'delivered', labelKey: 'status.delivered' },
  { value: 'cancelled', labelKey: 'status.cancelled' },
];

const PAYMENT_STATUS_OPTIONS: { value: TicketBrowsePaymentStatus; labelKey: string }[] = [
  { value: 'unpaid', labelKey: 'paymentStatus.unpaid' },
  { value: 'paid', labelKey: 'paymentStatus.paid' },
];

interface TicketFilterBarProps {
  filters: TicketFilters;
  onFiltersChange: (filters: TicketFilters) => void;
  locations: AssignedLocationResponse[];
}

export function TicketFilterBar({ filters, onFiltersChange, locations }: TicketFilterBarProps) {
  const { t } = useTranslation('tickets');
  const sortedLocations = [...locations].sort((a, b) => a.name.localeCompare(b.name));

  function handleLocationChange(value: string) {
    onFiltersChange({ ...filters, locationId: value });
  }

  function handleStatusChange(value: string) {
    onFiltersChange({ ...filters, status: value as TicketStatus | '' });
  }

  function handlePaymentStatusChange(value: string) {
    onFiltersChange({ ...filters, paymentStatus: value as TicketBrowsePaymentStatus | '' });
  }

  function handleFromChange(value: string) {
    onFiltersChange({ ...filters, from: value });
  }

  function handleToChange(value: string) {
    onFiltersChange({ ...filters, to: value });
  }

  function handleClear() {
    onFiltersChange(INITIAL_TICKET_FILTERS);
  }

  const hasActiveFilters =
    filters.locationId !== '' ||
    filters.status !== '' ||
    filters.paymentStatus !== '' ||
    filters.from !== '' ||
    filters.to !== '';

  return (
    <div className="flex flex-wrap items-end gap-3">
      {/* Location — role-scoped (backend already restricts `locations` to the
          actor's assignments for supervisor; org_admin sees the whole org). */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor="ticket-location"
          className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
        >
          {t('filters.location.label')}
        </label>
        <select
          id="ticket-location"
          value={filters.locationId}
          onChange={(e) => handleLocationChange(e.target.value)}
          className="h-9 rounded-md border border-jq-sand/70 bg-jq-surface-0 px-3 text-[13px] text-jq-charcoal focus:border-jq-navy/40 focus:outline-none focus:ring-1 focus:ring-jq-navy/20"
        >
          <option value="">{t('filters.location.all')}</option>
          {sortedLocations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name}
            </option>
          ))}
        </select>
      </div>

      {/* Status */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor="ticket-status"
          className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
        >
          {t('filters.status.label')}
        </label>
        <select
          id="ticket-status"
          value={filters.status}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="h-9 rounded-md border border-jq-sand/70 bg-jq-surface-0 px-3 text-[13px] text-jq-charcoal focus:border-jq-navy/40 focus:outline-none focus:ring-1 focus:ring-jq-navy/20"
        >
          <option value="">{t('filters.status.all')}</option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
      </div>

      {/* Payment status */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor="ticket-payment-status"
          className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
        >
          {t('filters.payment.label')}
        </label>
        <select
          id="ticket-payment-status"
          value={filters.paymentStatus}
          onChange={(e) => handlePaymentStatusChange(e.target.value)}
          className="h-9 rounded-md border border-jq-sand/70 bg-jq-surface-0 px-3 text-[13px] text-jq-charcoal focus:border-jq-navy/40 focus:outline-none focus:ring-1 focus:ring-jq-navy/20"
        >
          <option value="">{t('filters.payment.all')}</option>
          {PAYMENT_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
      </div>

      {/* From date */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor="ticket-from"
          className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
        >
          {t('filters.from')}
        </label>
        <Input
          id="ticket-from"
          type="date"
          value={filters.from}
          onChange={(e) => handleFromChange(e.target.value)}
          className="h-9 w-[160px] text-[13px]"
        />
      </div>

      {/* To date */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor="ticket-to"
          className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite"
        >
          {t('filters.to')}
        </label>
        <Input
          id="ticket-to"
          type="date"
          value={filters.to}
          onChange={(e) => handleToChange(e.target.value)}
          className="h-9 w-[160px] text-[13px]"
        />
      </div>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="h-9 text-[13px] text-jq-graphite"
        >
          <X className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
          {t('filters.clear')}
        </Button>
      )}
    </div>
  );
}
